/**
 * Tenant registry — config estática client-side.
 *
 * Fonte canônica oficial é a tabela `platform_tenants` no Supabase. Este
 * arquivo replica os campos que o frontend precisa em runtime (cor, nome,
 * rota base) sem precisar de fetch a cada renderização. Quando mudar status
 * de waitlist→active ou ajustar cor, atualizar AMBOS (DB + este arquivo).
 *
 * Ref: supabase/migrations/20260601000000_platform_schema.sql (seed)
 */

import type { PlatformTenantSlug } from "@/platform/lib/database.types";

export interface TenantConfig {
  slug: PlatformTenantSlug;
  name: string;
  shortName: string;
  course: string;
  banca: string;
  tagline: string;
  accent: { hsl: string; deepHsl: string; softHsl: string };
  routeBase: `/${PlatformTenantSlug}`;
  status: "active" | "waitlist";
}

const PLATFORM_NEUTRAL_ACCENT = {
  hsl: "255 78% 62%",
  deepHsl: "255 75% 45%",
  softHsl: "255 90% 96%",
};

export const TENANTS: Record<PlatformTenantSlug, TenantConfig> = {
  einstein: {
    slug: "einstein",
    name: "PsiEinstein PatternLab",
    shortName: "PsiEinstein",
    course: "Psicologia · Faculdade Einstein",
    banca: "Vunesp",
    tagline: "Domine o padrão Vunesp.",
    accent: {
      hsl: "255 78% 62%",
      deepHsl: "255 75% 45%",
      softHsl: "255 90% 96%",
    },
    routeBase: "/einstein",
    status: "active",
  },
  usp: {
    slug: "usp",
    name: "FuvestPro USP",
    shortName: "FuvestPro",
    course: "Engenharia de Produção · Poli-USP",
    banca: "Fuvest",
    tagline: "Engenharia reversa da Fuvest.",
    accent: {
      hsl: "222 65% 50%",
      deepHsl: "222 70% 35%",
      softHsl: "222 80% 95%",
    },
    routeBase: "/usp",
    status: "waitlist",
  },
};

/**
 * Lista de prefixos de rota que pertencem a cada tenant. Inclui as rotas
 * antigas Einstein no path root (legadas, sem prefixo /einstein) para
 * preservar bookmarks. Quando a Fase M2 do roadmap completar, adicionar
 * prefixos /einstein/* aqui também.
 */
const EINSTEIN_ROUTE_PREFIXES = [
  "/einstein",
  "/painel",
  "/plano",
  "/revisar",
  "/redacao",
  "/discursivas",
  "/simulador",
  "/evolucao",
  "/diagnostico",
  "/onboarding",
  "/metodo",
  "/banca-vunesp",
  "/admin/qa",
];

const USP_ROUTE_PREFIXES = ["/usp"];

/**
 * Detecta o tenant ativo a partir do pathname. Retorna null quando a rota
 * é puramente platform-level (hub, /precos, /sobre, /entrar, /termos, etc).
 */
export function detectTenantFromPath(pathname: string): PlatformTenantSlug | null {
  if (USP_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return "usp";
  }
  if (EINSTEIN_ROUTE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return "einstein";
  }
  return null;
}

export const PLATFORM_DEFAULT_ACCENT = PLATFORM_NEUTRAL_ACCENT;
